/** @module components/search-layout */
import _ from "underscore";
import baustein from "baustein";
import analytics from "web/script/analytics/analytics";
import diff from "web/script/utils/diff";
import browser from "web/script/utils/browser";
import rothko from "web/script/modules/rothko";
import environment from "web/script/modules/environment";
import userProfiler from "web/script/modules/userprofiler";
import history from "web/script/utils/history";
import style from "web/script/dom/style";
import store from "web/redux/store";
import { setActiveFilters } from "web/redux/ducks/feed-filters";

const THIN_FEED_PRODUCT_LIMIT = 7;
const CRITEO_PRODUCT_ID_LIMIT = 3;

/**
 * @constructor
 * @alias module:components/search-layout
 * @extends module:baustein
 */
export default baustein.register(
    "search-layout",
    /** @lends module:components/search-layout.prototype */
    {
        defaultOptions: function() {
            return {
                query: "",
                term: "",
                filters: {},
                metaFilters: {},
                page: 1,
                totalPageCount: 1,
                totalProductCount: 0,
                alternatives: [],
                view: "",
                hasResults: false,
            };
        },

        template: () =>
            import(
                /* webpackChunkName: "template-layouts-search-layout" */
                "templates/layouts/search_layout"
            ),

        // keep a reference here, to avoid changing options internally
        _totalPageCount: 0,

        /**
         * Transition to the right state.
         */
        init: function() {
            this._endOfSearchReached = false;
            this._isLoading = false;

            this._totalPageCount = this.options.totalPageCount;

            this._filtersClosePromise = null;

            var isServerRendered = environment.pop("search_is_server_rendered");

            this.searchLayoutClient = new rothko.Rothko("layouts/search_layout");
            this.searchLayoutClient.on("data", this.onSearchLayoutData.bind(this));

            if (this.options.hasResults) {
                userProfiler.saveSearchTerm(this.options.query);
            }
            if (isServerRendered) {
                const { totalPageCount, totalProductCount } = this.options;
                this._trackThinFeed(totalPageCount, totalProductCount);
                return;
            }

            this.searchLayoutClient.fetch(this.getApiQueryParams()).catch(function(e) {
                console.error(e);
            });

            if (!browser.supportSticky) {
                // if browser does not support 'position: sticky' use old buttons
                const topFilterButtons = this.el.querySelector(".search-layout__sticky-filter");
                if (topFilterButtons) {
                    topFilterButtons.classList.add("hidden");
                }
            }
        },

        setupEvents: function(add) {
            add("openFilters", this.openFilters);
            add("closeFilters", "mobile-filters-toolbar", this.closeUniversalFilters);
            add("applyFilters", this.applyUniversalFilters);
            add("feedSortingChanged", "sort-dropdown", this._onFeedSortingChanged);
            add("feedSortingChanged", "filter-button", this._onFeedSortingChanged);

            add("openOverlay", "product-card", this.openProductOverlay);
            add("selected", "product-card", this.onProductCardSelect);
        },

        onInsert() {
            this.emit("searchLayoutInserted");

            this.setGlobalHandler("scroll", this._onScroll);
            this._onScroll();

            if (this.options.page === 1) {
                this._trackCriteoProductIds();
            }
        },

        onRemove() {
            this.releaseGlobalHandler("scroll", this._onScroll);
        },

        /**
         * Handler for the changing of options
         */
        onOptionsChange: function(previousOptions) {
            var changes = diff(previousOptions, this.options);

            var searchInputChange = _.some(changes, function(record) {
                return _.contains(["query", "metaFilters"], record.key);
            });

            var paginationChange = _.findWhere(changes, {
                key: "page",
            });

            var filterChange = _.findWhere(changes, {
                key: "filters",
            });

            var sortChange = _.findWhere(changes, {
                key: "view",
            });

            var filterChanges = diff(previousOptions.filters, this.options.filters);

            if (searchInputChange) {
                this.enterSearchingLoadState();
            } else if (filterChange) {
                this.enterFilteringLoadState(filterChanges);
            } else if (paginationChange || sortChange) {
                this.enterPaginatingOrSortingLoadState();
            }

            this.searchLayoutClient.fetch(this.getApiQueryParams()).catch(function(e) {
                console.error(e);
            });
            this._loadNewFilters();
        },

        _loadNewFilters() {
            const existingFilters = this.findComponent("universal-filters");
            existingFilters.updateOptions({
                queryString: history.getCurrentQueryParams(),
            });
        },

        /**
         * Enter the searching load state
         */
        enterSearchingLoadState: function() {
            this.replaceRenderContext(this.getLoadingContext());
            this._endOfSearchReached = false;
            this._isLoading = true;
        },

        /**
         * Enter the filtering load state.
         * @param filterChanges A change array of the filters that changed.
         */
        enterFilteringLoadState: function(filterChanges) {
            var changedFilterKey = filterChanges[0].key || null;
            var pagination = this.findComponents("feed-pagination");
            var searchResults = this.findComponents("search-results");

            if (searchResults == null) {
                return;
            }

            var filters = this.findComponents("search-filter").filter(function(filter) {
                return filter.getFilterField() !== changedFilterKey;
            });

            this.invoke(filters.concat(pagination, searchResults), "updateOptions", {
                disabled: true,
            });
            this._isLoading = true;
        },

        /**
         * Enter the paginating or sorting loading state
         */
        enterPaginatingOrSortingLoadState: function() {
            var searchResults = this.findComponent("search-results");
            var pagination = this.findComponent("feed-pagination");

            if (searchResults == null) {
                return;
            }

            this.invoke([searchResults, pagination], "updateOptions", {
                disabled: true,
            });
            this._isLoading = true;

            setTimeout(browser.scrollTop, 200, 0, 250);
        },

        /**
         * This is similar to what happens in product-feed, we want to know when the user has
         * reached the end of the page. We first need to know what page the user is on as it's not
         * infinite.
         *
         * This has been debounced, as there is a scroll animation when you flick between pages that
         * could be captured.
         * @private
         */
        _onScroll: _.debounce(function() {
            const searchResults = this.findComponent("search-results");

            if (!searchResults) {
                return;
            }

            const bottomPos = style.getPosition(searchResults.el).bottom;
            const difference = browser.getWindowHeight() - bottomPos;

            const onLastPage = this.options.page === this._totalPageCount;

            if (onLastPage && !this._endOfSearchReached && !this._isLoading && difference > 0) {
                this._endOfSearchReached = true;
                analytics.event("ENGAGEMENT", "FEED_END", "END_OF_SEARCH_RESULTS_REACHED", true);
            }
        }, 100),

        /**
         * Handles the event from the 'filters' button shown on mobile. Opens the search-filters.
         */
        openFilters() {
            this.findComponent("search-results").hide();

            // call openFilters on universal filters
            const universalFiltersComponent = this.findComponent("universal-filters");
            this.invoke(universalFiltersComponent, "onOpenFilters");
        },

        /**
         * Handles the event from the universal-filters component to close the filters without applying them.
         * @param {object} event
         */
        closeUniversalFilters() {
            const results = this.findComponent("search-results");
            if (results) {
                results.show();
            }

            const filters = this.findComponent("universal-filters");
            this.invoke(filters, "closeFilters");
        },

        /**
         * Applies the filters based on the universal filters. There are two cases where this may
         * happen (automatically via selecting a filter, or by clicking the Apply button).
         *
         */
        applyUniversalFilters() {
            const searchResults = this.findComponent("search-results");
            if (searchResults) {
                searchResults.show();
            }
        },

        /**
         * Get the initial render context
         * @returns {object}
         */
        getInitialRenderContext: function() {
            return this.getLoadingContext();
        },

        /**
         * Get the loading context.
         * @returns {object}
         */
        getLoadingContext: function() {
            return {
                search_layout: {
                    is_loading: true,
                    summary_bar: {
                        showing_sentence: 'Loading results for "' + this.options.query + '"...',
                    },
                },
            };
        },

        /**
         * Make the query params needed to make call the search endpoint
         * @private
         */
        getApiQueryParams: function() {
            const params = _.extend(
                {
                    q: this.options.query,
                    page: this.options.page,
                    term: typeof this.options.term === "undefined" ? "" : this.options.term,
                    view: typeof this.options.view === "undefined" ? "" : this.options.view,
                },
                this.options.filters,
                this.options.metaFilters
            );

            if (this.options.alternatives) {
                params.alternatives = this.options.alternatives;
            }

            return params;
        },

        /**
         * Handles new data being available from the Rothko client.
         * @param {object} event
         * @param {object} searchLayoutData
         */
        onSearchLayoutData: function(event, searchLayoutData) {
            var searchResults = this.findComponent("search-results");
            var searchSummaryBar = this.findComponent("search-summary-bar");
            var feedPagination = this.findComponent("feed-pagination");
            var quickFilters = this.findComponent("quick-filters-card");

            // If the search term returned no results and we haven't already checked to see
            // if the 'did you mean?' api has any alternative suggestions, call the 'did you mean?'
            // api
            if (!searchLayoutData.has_results && !this.options.alternatives) {
                this.setRenderContext({
                    search_layout: searchLayoutData,
                });
                this._totalPageCount = searchLayoutData.total_page_count;
                this._isLoading = false;
                return;
            }

            this._filtersClosePromise = null;

            this._trackPageView(searchLayoutData);

            // If either of these components are not present then we will set the content for
            // this component which will cause it to render
            if (!searchResults || !searchSummaryBar) {
                let universalFilters;
                if (searchLayoutData.universal_filters) {
                    universalFilters = _.clone(searchLayoutData.universal_filters);
                    searchLayoutData.universal_filters.filters = [];
                }

                this.setRenderContext({
                    search_layout: searchLayoutData,
                });

                if (universalFilters) {
                    this.findComponent("universal-filters").setRenderContext({
                        universal_filters: universalFilters,
                    });
                    // put the original version back where it belongs
                    searchLayoutData.universal_filters = universalFilters;
                }

                this._totalPageCount = searchLayoutData.total_page_count;
                this._isLoading = false;

                if (this.options.page === 1) {
                    this._trackCriteoProductIds();
                }
                return;
            }

            // we want to render these things as fast as possible
            searchSummaryBar.setRenderContext({
                search_summary_bar: searchLayoutData.summary_bar,
            });
            searchResults.setRenderContext({
                search_results: searchLayoutData.results,
            });
            feedPagination.setRenderContext({
                pagination: searchLayoutData.pagination,
            });

            if (quickFilters) {
                quickFilters.updateOptions({
                    queryString: history.getCurrentQueryParams(),
                });
            }

            // We need to un-disable the searchResults and feedPagination
            this.invoke([searchResults, feedPagination], "updateOptions", {
                disabled: false,
            });

            this._isLoading = false;

            if (this.options.page === 1) {
                this._trackCriteoProductIds();
            }
        },

        /**
         * Updates the environment and tracks a new page view.
         * @param {Object} searchLayoutData
         * @private
         */
        _trackPageView: function(searchLayoutData) {
            var subType;

            if (!searchLayoutData.has_results) {
                subType = "empty";
            } else if (!_.isEmpty(this.options.filters)) {
                subType = "filtered";
            } else {
                subType = "unfiltered";
            }

            environment.set({
                pageType: "search",
                pageSubType: subType,
            });

            analytics.pageView();

            this._trackThinFeed(
                searchLayoutData.total_page_count,
                searchLayoutData.total_product_count
            );
        },

        /**
         * We want to track when thin feed results are returned.
         * @param totalPageCount
         * @param totalProductCount
         * @private
         */
        _trackThinFeed(totalPageCount, totalProductCount) {
            if (totalPageCount === 1 && totalProductCount < THIN_FEED_PRODUCT_LIMIT) {
                analytics.event("ENGAGEMENT", "FEED_END", "THIN_SEARCH_RESULTS", true);
            }
        },

        /**
         * Track top of page product ids for criteo remarketing.
         */
        _trackCriteoProductIds() {
            const productCards = this.findComponents("product-card");

            if (productCards.length > 0) {
                const criteoProductIds = [];

                productCards.slice(0, CRITEO_PRODUCT_ID_LIMIT).forEach((product) => {
                    criteoProductIds.push(product.options.productId);
                });

                analytics.tagEvent(analytics.TAG_NETWORKS.CRITEO, "criteo_product_ids", {
                    ids: criteoProductIds,
                });
            }
        },

        /**
         * Handles the `openOverlay` event fired by the product-card component.
         * @param {Object} event
         */
        openProductOverlay: function(event) {
            history.pushState(event.url, event.queryParams);
        },

        /**
         * Handler for the `selected` event from 'product-card' components. Tracks the index
         * of the product in the total set of results results e.g. taking page no. into account.
         * @param event
         */
        onProductCardSelect: function(event) {
            var productCard = event.target;
            var index = _.indexOf(this.findComponents("product-card"), productCard);

            // allow for pagination
            index += (this.options.page - 1) * 42;

            analytics.event("search_results", "product_card_select", index);
        },

        _onFeedSortingChanged(event) {
            const filters = this.findComponent("universal-filters");
            const appliedFilters = filters.getSerializedForm();
            appliedFilters["view"] = event.sortOption;
            store.dispatch(setActiveFilters(appliedFilters));
        },
    }
);
